/**
 * CreateLeagueModal — open from LeaguesPage or anywhere we surface
 * "Create a Friend League". On submit it calls api.createLeague and the
 * onCreated callback receives the new league summary so the caller can
 * navigate to /leagues/:id.
 *
 * The country-lock toggle is auto-disabled when the user's countryCode
 * is "XX" (worldwide / unset) — there's no meaningful country to lock to
 * in that case. Auth-store gate is upstream: anonymous users see a
 * sign-in nudge before this modal opens.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type LeagueSummary } from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { flag, resolveCountry } from "@/lib/countries";

interface CreateLeagueModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (league: LeagueSummary & { joinUrl: string }) => void;
}

export function CreateLeagueModal({ open, onClose, onCreated }: CreateLeagueModalProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [name, setName] = useState("");
  const [countryLockOn, setCountryLockOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setCountryLockOn(false);
      setError(null);
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const userCountry =
    user?.countryCode && user.countryCode !== "XX" ? user.countryCode : null;
  const userCountryName = userCountry ? resolveCountry(userCountry).name : null;

  const submit = async () => {
    if (busy) return;
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError(t("leagues.name_too_short"));
      return;
    }
    if (trimmed.length > 60) {
      setError(t("leagues.name_too_long"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { league } = await api.createLeague(
        trimmed,
        countryLockOn && userCountry ? userCountry : undefined,
      );
      onCreated(league);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError(t("leagues.signin_first"));
        else if (err.status === 403) {
          setError(t("leagues.free_limit"));
        } else if (err.status === 400 && (err.body as { error?: string })?.error === "country_lock_mismatch") {
          setError(t("leagues.country_lock_mismatch"));
        } else {
          setError(t("leagues.create_failed"));
        }
      } else {
        setError(t("leagues.create_failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-league-title"
      onClick={onClose}
    >
      <div
        className="w-[min(26rem,92%)] rounded-3xl bg-emerald-950/95 p-6 text-white shadow-2xl ring-1 ring-amber-300/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 id="create-league-title" className="text-xl font-bold">
            {t("leagues.create_modal_title")}
          </h2>
          <button
            type="button"
            aria-label={t("leagues.close")}
            className="opacity-60 hover:opacity-100"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <p className="mt-2 text-sm opacity-80">
          {t("leagues.create_modal_body")}
        </p>

        <label className="mt-5 block text-xs font-semibold uppercase tracking-wider opacity-70">
          {t("leagues.name_label")}
        </label>
        <input
          ref={inputRef}
          type="text"
          value={name}
          maxLength={60}
          placeholder={t("leagues.name_placeholder")}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
          className="mt-1 w-full rounded-xl bg-white/10 px-3 py-2 text-base outline-none ring-emerald-400/60 focus:ring-2"
        />

        {userCountry && (
          <label className="mt-4 flex items-start gap-3 rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
            <input
              type="checkbox"
              checked={countryLockOn}
              onChange={(e) => setCountryLockOn(e.target.checked)}
              className="mt-1"
            />
            <span className="flex-1 text-sm">
              <span className="block font-semibold">
                {flag(userCountry)} {t("leagues.country_lock_label", { country: userCountryName })}
              </span>
              <span className="block text-xs opacity-70">
                {t("leagues.country_lock_body", { country: userCountryName })}
              </span>
            </span>
          </label>
        )}

        {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}

        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            disabled={busy}
            className="rounded-full bg-amber-300 px-5 py-2.5 font-semibold text-emerald-950 transition hover:bg-amber-200 disabled:opacity-50"
            onClick={submit}
          >
            {busy ? t("leagues.creating") : t("leagues.create_button_confirm")}
          </button>
          <button
            type="button"
            disabled={busy}
            className="rounded-full bg-white/5 px-5 py-2 text-sm opacity-70 hover:opacity-100 disabled:opacity-30"
            onClick={onClose}
          >
            {t("leagues.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
